import { getAuthAppOrigin } from "@/lib/authOrigin";

export type AuthRedirectKey =
  | "auth-callback"
  | "dashboard"
  | "admin"
  | "reset-password"
  | "onboarding"
  | "home"
  | "store-account";

const SLUG_SAFE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

/**
 * Absolute URLs for Supabase emailRedirectTo / redirectTo (always main app origin).
 */
export function getAuthRedirect(
  type: AuthRedirectKey,
  options?: { slug?: string | null }
): string {
  const origin = getAuthAppOrigin();

  switch (type) {
    case "auth-callback":
      return `${origin}/auth/callback`;
    case "dashboard":
      return `${origin}/dashboard`;
    case "admin":
      return `${origin}/admin`;
    case "reset-password":
      return `${origin}/reset-password`;
    case "onboarding":
      return `${origin}/onboarding`;
    case "home":
      return `${origin}/`;
    case "store-account": {
      const slug = options?.slug?.trim() ?? "";
      if (!slug) {
        if (import.meta.env.DEV) {
          console.warn(
            '[auth] getAuthRedirect("store-account") called without slug — using home'
          );
        }
        return `${origin}/`;
      }
      if (!SLUG_SAFE.test(slug)) {
        if (import.meta.env.DEV) {
          console.warn('[auth] getAuthRedirect("store-account") invalid slug — using home', slug);
        }
        return `${origin}/`;
      }
      return `${origin}/store/${encodeURIComponent(slug)}/account`;
    }
    default:
      throw new Error(`[auth] unknown redirect type: ${String(type)}`);
  }
}

/**
 * Ensures URLs passed to Supabase auth use the configured main origin and an allowlisted path.
 */
export function validateAuthRedirectForSupabase(fullUrl: string): void {
  const origin = getAuthAppOrigin();
  let parsed: URL;
  try {
    parsed = new URL(fullUrl);
  } catch {
    throw new Error("[auth] Invalid redirect URL (could not parse)");
  }

  if (parsed.origin !== origin) {
    const msg = `[auth] Redirect URL origin must be ${origin}`;
    if (import.meta.env.PROD) throw new Error(msg);
    console.warn(msg, fullUrl);
    return;
  }

  const path =
    parsed.pathname.length > 1 && parsed.pathname.endsWith("/")
      ? parsed.pathname.slice(0, -1)
      : parsed.pathname;

  const ok =
    path === "" ||
    path === "/" ||
    path === "/auth/callback" ||
    path === "/dashboard" ||
    path === "/dashboard-v2" ||
    path.startsWith("/dashboard/") ||
    path.startsWith("/dashboard-v2/") ||
    path === "/admin" ||
    path === "/reset-password" ||
    path === "/onboarding" ||
    /^\/store\/[a-z0-9-]+\/account$/i.test(path);

  if (!ok) {
    const msg = `[auth] Redirect path not allowlisted for Supabase: ${path}`;
    if (import.meta.env.PROD) throw new Error(msg);
    console.warn(msg);
  }
}

/** Same as getAuthRedirect + validate (use before every Supabase redirect). */
export function getValidatedAuthRedirect(
  type: AuthRedirectKey,
  options?: { slug?: string | null }
): string {
  const url = getAuthRedirect(type, options);
  validateAuthRedirectForSupabase(url);
  return url;
}

/** Build buyer post-verify URL: store account if slug ok, else auth callback for session handling. */
export function getBuyerEmailRedirectUrl(slug: string | null | undefined): string {
  const s = slug?.trim() ?? "";
  if (s && SLUG_SAFE.test(s)) {
    return getValidatedAuthRedirect("store-account", { slug: s });
  }
  if (import.meta.env.DEV && !s) {
    console.warn("[auth] buyer email redirect: missing store slug — using auth-callback");
  }
  return getValidatedAuthRedirect("auth-callback");
}
